"use client";

import { useMemo } from "react";
import type { RecordingMode } from "../types";
import { useRecordingEngine } from "./use-recording-engine";
import { useAudioRecording } from "./use-audio-recording";
import { useVideoRecording } from "./use-video-recording";
import { useScreenRecording } from "./use-screen-recording";

interface UseRecordingOptions {
  mode?: RecordingMode;
  isVideo?: boolean; // deprecated alias — resolved as mode === "video"
  withCameraOverlay?: boolean;
  onAudioData?: (data: ArrayBuffer) => void;
  videoQuality?: string;
  /** Returns true when the live-transcription socket is ready to receive audio data. */
  getSocketState?: () => boolean;
  /** Returns the live-transcription hook's specific error if any. */
  getSocketError?: () => string | null;
  /** Owner of the take saved in this browser while recording. */
  userId?: string;
}

export function useRecording(options: UseRecordingOptions = {}) {
  const { mode, isVideo, withCameraOverlay, onAudioData, videoQuality, getSocketState, getSocketError, userId } = options;

  // mode takes precedence; isVideo is the deprecated alias kept for backward compat
  const resolvedMode: RecordingMode = mode ?? (isVideo ? "video" : "audio");

  // ONE engine instance — eliminates triple enumerateDevices() on mount
  const engine = useRecordingEngine({ userId });

  // Stable option objects — scalars in deps prevent new references on every render,
  // which would otherwise bust useCallback deps in each mode hook.
  const audioOptions = useMemo(
    () => ({ onDataAvailable: onAudioData, getSocketState, getSocketError }),
    [onAudioData, getSocketState, getSocketError],
  );
  const videoOptions = useMemo(
    () => ({ videoQuality }),
    [videoQuality],
  );
  const screenOptions = useMemo(
    () => ({ withCameraOverlay }),
    [withCameraOverlay],
  );

  // All three per-mode hooks called unconditionally (rules of hooks).
  // Inactive hooks receive isActive=false and return zero-state.
  const audioResult = useAudioRecording(engine, resolvedMode === "audio", audioOptions);
  const videoResult = useVideoRecording(engine, resolvedMode === "video", videoOptions);
  const screenResult = useScreenRecording(engine, resolvedMode === "screen", screenOptions);

  switch (resolvedMode) {
    case "video":
      return { ...videoResult, mode: resolvedMode as RecordingMode };
    case "screen":
      return { ...screenResult, mode: resolvedMode as RecordingMode };
    default:
      return { ...audioResult, mode: resolvedMode as RecordingMode };
  }
}
